import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  MenuItem,
  Divider,
  Alert,
  AlertTitle,
} from '@mui/material';
import { ArrowBack, Send } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { branchAPI } from '../../../services/api';

const BranchCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Branch Details
    name: '',
    // code is auto-generated based on district

    // Address
    street: '',
    city: '',
    district: '',
    pincode: '',

    // Contact Person
    contactPersonName: '',
    contactPersonEmail: '',
    contactPersonPhone: '',

    // Branch Admin
    adminName: '',
    adminEmail: '',
    adminPhone: '',
  });

  const districts = [
    'Thiruvananthapuram', 'Kollam', 'Pathanamthitta', 'Alappuzha',
    'Kottayam', 'Idukki', 'Ernakulam', 'Thrissur', 'Palakkad',
    'Malappuram', 'Kozhikode', 'Wayanad', 'Kannur', 'Kasargod'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.district) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.adminName || !formData.adminEmail) {
      toast.error('Branch admin name and email are required');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.adminEmail)) {
      toast.error('Please enter a valid admin email address');
      return;
    }

    if (formData.contactPersonEmail && !emailRegex.test(formData.contactPersonEmail)) {
      toast.error('Please enter a valid contact person email address');
      return;
    }

    try {
      setLoading(true);

      const payload = {
        name: formData.name,
        // code will be auto-generated by backend
        address: {
          street: formData.street,
          city: formData.city,
          district: formData.district,
          state: 'Kerala',
          pincode: formData.pincode,
        },
        contactPerson: {
          name: formData.contactPersonName,
          email: formData.contactPersonEmail,
          phone: formData.contactPersonPhone,
        },
        adminName: formData.adminName,
        adminEmail: formData.adminEmail,
        adminPhone: formData.adminPhone,
      };

      const response = await branchAPI.create(payload);

      toast.success(
        `Branch created successfully with code: ${response.data.code}. Login credentials sent to admin via email.`,
        { autoClose: 6000 }
      );

      // Navigate to branch list after short delay
      setTimeout(() => {
        navigate('/admin/branches');
      }, 2000);
    } catch (error) {
      console.error('Error creating branch:', error);
      toast.error(error.response?.data?.message || 'Failed to create branch');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Create New Branch / ATC Center
        </Typography>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/admin/branches')}
        >
          Back to Branches
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>Automated Credential System</AlertTitle>
        A temporary password will be automatically generated and sent to the branch admin's email along with legal disclaimers and terms of use. The admin will be required to change the password upon first login.
      </Alert>

      <Paper elevation={2} sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Branch Information */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Branch Information
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Branch Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                fullWidth
                placeholder="e.g., BSS ATC Trivandrum"
              />
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info" variant="outlined">
                <strong>Branch Code Auto-Generation:</strong> A unique branch code will be automatically generated based on the selected district (e.g., BSS-TVM-001, BSS-EKM-002). You don't need to provide a code manually.
              </Alert>
            </Grid>

            {/* Address */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 2 }}>
                Branch Address
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Street Address"
                name="street"
                value={formData.street}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
                placeholder="Enter complete street address"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="City/Town"
                name="city"
                value={formData.city}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                select
                label="District"
                name="district"
                value={formData.district}
                onChange={handleChange}
                required
                fullWidth
              >
                {districts.map((district) => (
                  <MenuItem key={district} value={district}>
                    {district}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Pincode"
                name="pincode"
                value={formData.pincode}
                onChange={handleChange}
                fullWidth
                placeholder="e.g., 695001"
                inputProps={{ maxLength: 6, pattern: '[0-9]{6}' }}
              />
            </Grid>

            {/* Contact Person */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 2 }}>
                Contact Person (Optional)
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Contact Person Name"
                name="contactPersonName"
                value={formData.contactPersonName}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Contact Email"
                name="contactPersonEmail"
                type="email"
                value={formData.contactPersonEmail}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Contact Phone"
                name="contactPersonPhone"
                value={formData.contactPersonPhone}
                onChange={handleChange}
                fullWidth
                placeholder="e.g., +91-9876543210"
              />
            </Grid>

            {/* Branch Admin */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 2 }}>
                Branch Administrator
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Alert severity="warning" sx={{ mb: 3 }}>
                <strong>Important:</strong> An automatic email will be sent to this email address with login credentials and legal terms. Please ensure the email address is correct.
              </Alert>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Admin Full Name"
                name="adminName"
                value={formData.adminName}
                onChange={handleChange}
                required
                fullWidth
                placeholder="Full name of branch administrator"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Admin Email"
                name="adminEmail"
                type="email"
                value={formData.adminEmail}
                onChange={handleChange}
                required
                fullWidth
                placeholder="admin@example.com"
                helperText="Temporary password will be sent to this email"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Admin Phone"
                name="adminPhone"
                value={formData.adminPhone}
                onChange={handleChange}
                fullWidth
                placeholder="+91-9876543210"
              />
            </Grid>

            {/* Submit Buttons */}
            <Grid item xs={12}>
              <Divider sx={{ my: 3 }} />
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/admin/branches')}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={loading ? null : <Send />}
                  disabled={loading}
                >
                  {loading ? 'Creating Branch & Sending Email...' : 'Create Branch & Send Credentials'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default BranchCreate;
